"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import Image from "next/image";
import { storyblokEditable } from "@storyblok/react/rsc";
import Autoplay from "embla-carousel-autoplay";
import { useLightboxOpen } from "@/components/layout/LightboxProvider";

import { GridContainer } from "@/components/layout/GridContainer";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  GalleryNavPrevious,
  GalleryNavNext,
  type CarouselApi,
} from "@/components/ui/carousel";

type ImageGalleryStoryblok = {
  images?: {
    filename: string;
    alt?: string;
    id: number;
    [k: string]: any;
  }[];
  _uid: string;
  component: "image_gallery";
  [k: string]: any;
};

type ImageGalleryProps = {
  blok: ImageGalleryStoryblok;
};

const GalleryImage = ({
  image,
  groupId,
  className,
  sizes,
}: {
  image: { filename: string; alt?: string };
  groupId: string;
  className?: string;
  sizes: string;
}) => (
  <div
    className={`relative overflow-hidden rounded-3xl cursor-pointer ${className || ""}`}
    data-lightbox-src={image.filename}
    data-lightbox-alt={image.alt || ""}
    data-lightbox-group={groupId}
  >
    <Image
      src={image.filename}
      alt={image.alt || ""}
      fill
      sizes={sizes}
      className="object-cover"
    />
  </div>
);

const StaticGrid = ({
  images,
  blok,
}: {
  images: ImageGalleryStoryblok["images"] & {};
  blok: ImageGalleryStoryblok;
}) => {
  const count = images.length;

  if (count === 1) {
    return (
      <GridContainer className="!px-0">
        <div className="col-span-12 lg:col-span-10 lg:col-start-2">
          <GalleryImage
            image={images[0]}
            groupId={blok._uid}
            className="aspect-video lg:aspect-[21/9]"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 83vw, 1000px"
          />
        </div>
      </GridContainer>
    );
  }

  if (count === 2) {
    return (
      <GridContainer className="!px-0 !gap-4">
        <div className="col-span-12 md:col-span-6 lg:col-span-5 lg:col-start-2">
          <GalleryImage
            image={images[0]}
            groupId={blok._uid}
            className="aspect-video"
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-5">
          <GalleryImage
            image={images[1]}
            groupId={blok._uid}
            className="aspect-video"
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        </div>
      </GridContainer>
    );
  }

  if (count === 3) {
    return (
      <div className="flex flex-col gap-4">
        <GridContainer className="!px-0 !gap-4">
          <div className="col-span-12 lg:col-span-10 lg:col-start-2">
            <GalleryImage
              image={images[0]}
              groupId={blok._uid}
              className="aspect-[21/9] lg:aspect-[32/9]"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 83vw, 1000px"
            />
          </div>
        </GridContainer>
        <GridContainer className="!px-0 !gap-4">
          <div className="col-span-6 lg:col-span-5 lg:col-start-2">
            <GalleryImage
              image={images[1]}
              groupId={blok._uid}
              className="aspect-video"
              sizes="(max-width: 768px) 50vw, 42vw"
            />
          </div>
          <div className="col-span-6 lg:col-span-5">
            <GalleryImage
              image={images[2]}
              groupId={blok._uid}
              className="aspect-video"
              sizes="(max-width: 768px) 50vw, 42vw"
            />
          </div>
        </GridContainer>
      </div>
    );
  }

  // count === 4
  return (
    <div className="flex flex-col gap-4">
      <GridContainer className="!px-0 !gap-4">
        <div className="col-span-6">
          <GalleryImage
            image={images[0]}
            groupId={blok._uid}
            className="aspect-video"
            sizes="(max-width: 768px) 50vw, 50vw"
          />
        </div>
        <div className="col-span-6">
          <GalleryImage
            image={images[1]}
            groupId={blok._uid}
            className="aspect-video"
            sizes="(max-width: 768px) 50vw, 50vw"
          />
        </div>
      </GridContainer>
      <GridContainer className="!px-0 !gap-4">
        <div className="col-span-6">
          <GalleryImage
            image={images[2]}
            groupId={blok._uid}
            className="aspect-video"
            sizes="(max-width: 768px) 50vw, 50vw"
          />
        </div>
        <div className="col-span-6">
          <GalleryImage
            image={images[3]}
            groupId={blok._uid}
            className="aspect-video"
            sizes="(max-width: 768px) 50vw, 50vw"
          />
        </div>
      </GridContainer>
    </div>
  );
};

const ImageGallery = ({ blok }: ImageGalleryProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [canScroll, setCanScroll] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const lightboxOpen = useLightboxOpen();
  const autoplayRef = useRef(
    Autoplay({
      delay: 3000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    })
  );

  const images = blok.images || [];

  // Check if carousel can scroll (i.e., content overflows)
  const checkCanScroll = useCallback(() => {
    if (!api) return;
    const canScrollNext = api.canScrollNext();
    const canScrollPrev = api.canScrollPrev();
    setCanScroll(canScrollNext || canScrollPrev);
  }, [api]);

  useEffect(() => {
    if (!api) return;

    checkCanScroll();

    api.on("resize", checkCanScroll);
    api.on("reInit", checkCanScroll);

    return () => {
      api.off("resize", checkCanScroll);
      api.off("reInit", checkCanScroll);
    };
  }, [api, checkCanScroll]);

  // Track viewport visibility
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Control autoplay based on canScroll and viewport visibility
  useEffect(() => {
    const autoplay = autoplayRef.current;
    if (!api) return;

    if (canScroll && isInView && !lightboxOpen) {
      autoplay.play();
    } else {
      autoplay.stop();
    }
  }, [api, canScroll, isInView, lightboxOpen]);

  if (images.length === 0) return null;

  // Static grid layout for 1-4 images
  if (images.length <= 4) {
    return (
      <div ref={sectionRef} {...storyblokEditable(blok)} className="w-full">
        <section className="py-6">
          <StaticGrid images={images} blok={blok} />
        </section>
      </div>
    );
  }

  // Carousel for 5+ images
  return (
    <div ref={sectionRef} {...storyblokEditable(blok)} className="w-full">
      <section className="py-6 flex flex-col gap-4 w-full">
        <Carousel
          setApi={setApi}
          opts={{
            align: "center",
            loop: false,
            dragFree: false,
            watchResize: true,
            breakpoints: {
              "(min-width: 768px)": {
                align: "start",
                dragFree: true,
              },
            },
          }}
          plugins={[autoplayRef.current]}
          className="w-full relative"
        >
          <GalleryNavPrevious show={canScroll} />
          <CarouselContent
            className={
              canScroll
                ? "ml-0 pl-4 md:pl-8 items-stretch"
                : "justify-center items-stretch"
            }
          >
            {images.map((image, index) => (
              <CarouselItem
                key={`${image.id}-${index}`}
                className={
                  canScroll
                    ? "pl-4 basis-auto h-auto"
                    : "basis-auto px-2 h-auto"
                }
              >
                <div
                  className="relative h-[210px] w-[393px] max-w-[80vw] overflow-hidden rounded-3xl cursor-pointer"
                  data-lightbox-src={image.filename}
                  data-lightbox-alt={image.alt || ""}
                  data-lightbox-group={blok._uid}
                >
                  <Image
                    src={image.filename}
                    alt={image.alt || ""}
                    fill
                    sizes="393px"
                    className="object-cover"
                  />
                </div>
              </CarouselItem>
            ))}
            {canScroll && <div className="shrink-0 w-4 md:w-8" />}
          </CarouselContent>
          <GalleryNavNext show={canScroll} />
        </Carousel>
      </section>
    </div>
  );
};

export default ImageGallery;
